import React, { useCallback, useEffect, useState } from 'react';
import { Auth as _Auth } from '~/amplify';
import { navigate, useLocation, WindowLocation } from '@gatsbyjs/reach-router';
import getEmailAndPasswordFromMagicLink from '~/components/page/Auth/Login/utils/getEmailAndPasswordFromMagicLink';
import type { WindowState } from '~/components/page/Auth/Login';
import { AUTH_PATHS } from '~/components/page/Auth/constants';
import useLogout from '~/hooks/useLogout';
import { isExpiredOrNotAuthorizedSessionError } from '~/Errors';
import useIsMounted from '~/hooks/useIsMounted';
import getIsMFACompleted from '~/util/getIsMFACompleted';
import isUnauthorizedPath from './AppSyncProvider/hooks/useDHApolloClient/utils/isUnauthorizedPath';

type CognitoUser = {
  username: string;
  /** Currently we only have one group: ['DH_CROCS_ADMIN'] (internal DH admin, e.g can impersonate) */
  cognitoGroups: Array<string>;
  /** Only set to true after complete sign in with MFA (mfa is setup and code is verified) */
  mfaSetupComplete: boolean;
  attributes: {
    name: string | null;
    phoneNumber: string | null;
    phoneVerified: boolean | null;
  };
};

type AuthContextProps = {
  isLoggedIn: boolean;
  ensureLogin: (state?: { from?: string }) => void;
  user: CognitoUser | null;
  /** Checks if the user is DH_CROCS_ADMIN */
  isDHAdmin: boolean;
};

export const AuthContext = React.createContext<AuthContextProps>({
  isLoggedIn: false,
  user: null,
  ensureLogin: () => {},
  isDHAdmin: false,
});

type Props = {
  children: React.ReactNode;
};

const Auth: React.FCC<Props> = ({ children }) => {
  const [isValidatingLogin, setValidateLogin] = useState(true);
  const [user, setUser] = useState<CognitoUser | null>(null);
  const logout = useLogout();
  const isMounted = useIsMounted();

  const ensureLogin = useCallback(() => setValidateLogin(true), []);
  const location = useLocation() as WindowLocation<
    { from?: string } | undefined
  >;
  const isLoggedIn = user !== null;
  const [magicLinkProcessed, setMagicLinkProcessed] = useState(false);

  useEffect(() => {
    if (!isMounted()) return;

    const verifyCredentials = async () => {
      if (location) {
        const credentials = getEmailAndPasswordFromMagicLink(
          location as WindowLocation<WindowState>,
        );

        if (credentials.email && credentials.password && !magicLinkProcessed) {
          // If magic link credentials exist, log out current user
          try {
            await logout();

            setMagicLinkProcessed(true);
            setValidateLogin(false);
            setUser(null);
          } catch {
            // Handle error gracefully without logging
            setValidateLogin(false); // Ensure we don't get stuck in validating state
          }
          return;
        }
      }

      try {
        const currentUser = await _Auth.getCurrentUser();
        const session = await _Auth.fetchAuthSession();
        const isMFACompleted = await getIsMFACompleted();

        const cognitoGroups =
          (session.tokens?.accessToken?.payload?.[
            'cognito:groups'
          ] as Array<string>) || [];

        const attributes = await _Auth.fetchUserAttributes();

        const user: CognitoUser = {
          username: currentUser.username,
          cognitoGroups,
          mfaSetupComplete: isMFACompleted,
          attributes: {
            name: attributes?.name || null,
            phoneNumber: attributes?.phone_number || null,
            phoneVerified: attributes?.phone_number_verified === 'true' || null,
          },
        };

        setUser(user);
        setValidateLogin(false);
      } catch (error) {
        if (isExpiredOrNotAuthorizedSessionError(error)) {
          try {
            await logout();
          } catch {
            // Ignore logout errors; we still clear local state
          }
        }
        setUser(null);
        setValidateLogin(false);
      }
    };

    if (isValidatingLogin) {
      void verifyCredentials();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidatingLogin]);

  if (isValidatingLogin) return null;

  const isUnauthorizedRoute = isUnauthorizedPath(location.pathname);
  const isMFAUnauthorizedRoute =
    location.pathname.includes(AUTH_PATHS.MFA_SETUP_TOTP) ||
    location.pathname.includes(AUTH_PATHS.MFA_SETUP_PHONE) ||
    location.pathname.includes(AUTH_PATHS.ACCOUNT_RECOVERY);

  if (isUnauthorizedRoute && isLoggedIn && !isMFAUnauthorizedRoute) {
    /**
     * We are logged in but are on an auth route.
     * Redirect to the dashboard, or the page they originated from
     * before they went through the auth process.
     *
     * E.g. arrive at /tasks?officeId=null (but are unauthorised) -> /login -> /tasks?officeId=null
     *
     * Note: Users without MFA setup are allowed to continue using their existing session.
     * MFA setup will be enforced on their next login via Cognito's CONTINUE_SIGN_IN_WITH_TOTP_SETUP step.
     */
    void navigate(location.state?.from ?? '/-/', { replace: true }).then(() => {
      ensureLogin();
    });

    return null;
  }

  if (!isUnauthorizedRoute && !isLoggedIn) {
    /**
     * The user is not logged in and not on an auth route, we redirect them to /login
     *
     * We set the from state to redirect back to after the login
     * has happened. Except when we are on the logout route.
     * Cause endless loop.
     */
    const state = location.pathname.startsWith(AUTH_PATHS.LOGOUT)
      ? {}
      : { from: location.pathname + location.search };

    void navigate('/login', {
      replace: true,
      state,
    });
    return null;
  }

  const isDHAdmin = user?.cognitoGroups.includes('DH_CROCS_ADMIN') || false;

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: user != null,
        user,
        isDHAdmin,
        ensureLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default Auth;
